import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

export type DashboardKpi = {
  description: string;
  value: string;
};

const labelToSlug = (label: string) =>
  label === '#'
    ? 'loans'
    : label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

const formatKpiLabel = (description: string) => (description === '#' ? '# LOANS' : description);

const SCROLL_STEP_PX = 160;
const ARROW_SIZE_PX = 32;
const ARROW_GAP_PX = 10;
/** Places arrow fully outside the scroll edge with a 10px gap before/after content. */
const ARROW_OUTSIDE_OFFSET_PX = -(ARROW_SIZE_PX + ARROW_GAP_PX);

type ScrollState = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
};

const computeLeftArrowOffset = (trackEl: HTMLElement, primaryEl: HTMLElement): number => {
  const trackRect = trackEl.getBoundingClientRect();
  const primaryRect = primaryEl.getBoundingClientRect();

  const primaryLeftFromTrack = primaryRect.left - trackRect.left;
  return primaryLeftFromTrack - ARROW_GAP_PX - ARROW_SIZE_PX;
};

const computeRightArrowOffset = (trackEl: HTMLElement, lastEl: HTMLElement): number => {
  const trackRect = trackEl.getBoundingClientRect();
  const lastRect = lastEl.getBoundingClientRect();

  const lastExtendsPastViewport = lastRect.right > trackRect.right + 1;
  return lastExtendsPastViewport
    ? ARROW_OUTSIDE_OFFSET_PX
    : trackRect.right - lastRect.right - ARROW_GAP_PX - ARROW_SIZE_PX;
};

const useArrowOffsets = (contentKey: string) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLDivElement>(null);
  const firstCardRef = useRef<HTMLDivElement>(null);
  const lastCardRef = useRef<HTMLDivElement>(null);
  const [leftOffset, setLeftOffset] = useState(ARROW_OUTSIDE_OFFSET_PX);
  const [rightOffset, setRightOffset] = useState(ARROW_OUTSIDE_OFFSET_PX);

  const updateArrowOffsets = useCallback(() => {
    const track = trackRef.current;
    const primary = primaryRef.current;
    const last = lastCardRef.current;
    if (!track || !primary || !last) {
      return;
    }

    setLeftOffset(computeLeftArrowOffset(track, primary));
    setRightOffset(computeRightArrowOffset(track, last));
  }, []);

  useEffect(() => {
    if (!contentKey) {
      return;
    }

    updateArrowOffsets();

    const track = trackRef.current;
    const primary = primaryRef.current;
    const first = firstCardRef.current;
    const last = lastCardRef.current;
    if (!track || !primary || !first || !last) {
      return;
    }

    const observer = new ResizeObserver(updateArrowOffsets);
    observer.observe(track);
    observer.observe(primary);
    observer.observe(first);
    observer.observe(last);

    return () => observer.disconnect();
  }, [contentKey, updateArrowOffsets]);

  return {
    trackRef,
    primaryRef,
    firstCardRef,
    lastCardRef,
    leftOffset,
    rightOffset,
    updateArrowOffsets,
  };
};

const useHorizontalScrollState = (contentKey: string) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<ScrollState>({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const hasOverflow = maxScrollLeft > 1;

    setScrollState({
      canScrollLeft: hasOverflow && el.scrollLeft > 1,
      canScrollRight: hasOverflow && el.scrollLeft < maxScrollLeft - 1,
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    updateScrollState();

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);

    return () => observer.disconnect();
  }, [contentKey, updateScrollState]);

  const scrollByStep = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    el.scrollBy({
      left: direction === 'left' ? -SCROLL_STEP_PX : SCROLL_STEP_PX,
      behavior: 'smooth',
    });
  }, []);

  return { scrollRef, scrollState, updateScrollState, scrollByStep };
};

type ScrollArrowButtonProps = {
  direction: 'left' | 'right';
  onClick: () => void;
  'aria-label': string;
  offsetPx: number;
};

const ScrollArrowButton: FC<ScrollArrowButtonProps> = ({
  direction,
  onClick,
  'aria-label': ariaLabel,
  offsetPx,
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    style={direction === 'left' ? { left: offsetPx } : { right: offsetPx }}
    className="absolute top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-gray-700 bg-gray-950 text-gray-200 shadow-md transition-colors hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-400"
  >
    {direction === 'left' ? <IconChevronLeft size={16} /> : <IconChevronRight size={16} />}
  </button>
);

const ScrollEdgeFade: FC<{ side: 'left' | 'right'; visible: boolean }> = ({ side, visible }) => {
  if (!visible) {
    return null;
  }

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute top-0 z-[15] h-full w-6 ${
        side === 'left'
          ? 'to-transparent left-0 bg-gradient-to-r from-background-canvas/40'
          : 'to-transparent right-0 bg-gradient-to-l from-background-canvas/40'
      }`}
    />
  );
};

type DashboardKpiRowProps = {
  kpis: DashboardKpi[];
  'data-tour'?: string;
  'data-testid-prefix'?: string;
};

const PrimaryKpiCard: FC<{
  kpi: DashboardKpi;
  testId?: string;
}> = ({ kpi, testId }) => (
  <div
    className="flex h-16 min-w-48 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-[#260626] bg-[#e028dd] bg-gradient-to-r from-[#1555ec] to-[#ff23e1] p-2 text-center"
    data-testid={testId}
  >
    <div className="font-mono text-xl text-gray-100">{kpi.value}</div>
    <div>{formatKpiLabel(kpi.description)}</div>
  </div>
);

const SecondaryKpiCard: FC<{
  kpi: DashboardKpi;
  testId?: string;
}> = ({ kpi, testId }) => (
  <div
    className="flex h-16 min-w-32 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-gray-500 bg-gray-800 p-2 text-center"
    data-testid={testId}
  >
    <div className="font-mono text-xl text-gray-100">{kpi.value}</div>
    <div>{formatKpiLabel(kpi.description)}</div>
  </div>
);

/**
 * KPI row with a fixed primary pill and a separately scrollable secondary strip.
 * Left arrow sits 10px left of the primary pill; right arrow 10px past the last card.
 */
export const DashboardKpiRow: FC<DashboardKpiRowProps> = ({
  kpis,
  'data-tour': dataTour,
  'data-testid-prefix': testIdPrefix,
}) => {
  const primaryKpi = kpis[0];
  const secondaryKpis = kpis.slice(1);
  const secondaryContentKey = secondaryKpis
    .map((kpi) => `${kpi.description}:${kpi.value}`)
    .join('|');
  const { scrollRef, scrollState, updateScrollState, scrollByStep } =
    useHorizontalScrollState(secondaryContentKey);
  const {
    trackRef,
    primaryRef,
    firstCardRef,
    lastCardRef,
    leftOffset,
    rightOffset,
    updateArrowOffsets,
  } = useArrowOffsets(secondaryContentKey);

  if (!primaryKpi) {
    return null;
  }

  const lastIndex = secondaryKpis.length - 1;

  return (
    <div className="flex w-full min-w-0 items-stretch gap-4" data-tour={dataTour}>
      <div ref={primaryRef} className="shrink-0">
        <PrimaryKpiCard
          kpi={primaryKpi}
          testId={
            testIdPrefix ? `${testIdPrefix}-${labelToSlug(primaryKpi.description)}` : undefined
          }
        />
      </div>

      {secondaryKpis.length > 0 && (
        <div ref={trackRef} className="relative min-w-0 flex-1 overflow-visible">
          <ScrollEdgeFade side="left" visible={scrollState.canScrollLeft} />
          <ScrollEdgeFade side="right" visible={scrollState.canScrollRight} />
          {scrollState.canScrollLeft && (
            <ScrollArrowButton
              direction="left"
              aria-label="Scroll metrics left"
              offsetPx={leftOffset}
              onClick={() => scrollByStep('left')}
            />
          )}
          {scrollState.canScrollRight && (
            <ScrollArrowButton
              direction="right"
              aria-label="Scroll metrics right"
              offsetPx={rightOffset}
              onClick={() => scrollByStep('right')}
            />
          )}
          <div
            ref={scrollRef}
            onScroll={() => {
              updateScrollState();
              updateArrowOffsets();
            }}
            className="flex h-16 flex-row items-center gap-4 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {secondaryKpis.map((kpi, index) => (
              <div
                key={kpi.description}
                ref={index === 0 ? firstCardRef : index === lastIndex ? lastCardRef : undefined}
              >
                <SecondaryKpiCard
                  kpi={kpi}
                  testId={
                    testIdPrefix ? `${testIdPrefix}-${labelToSlug(kpi.description)}` : undefined
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const DashboardKpiRowSkeleton: FC<{ secondaryCount?: number }> = ({
  secondaryCount = 7,
}) => (
  <div className="flex w-full min-w-0 items-stretch gap-4">
    <div className="flex h-16 min-w-48 shrink-0 animate-pulse flex-col items-center justify-center gap-1 rounded-lg border border-[#260626] bg-gradient-to-r from-[#1555ec] to-[#ff23e1] p-2 opacity-50">
      <div className="h-6 w-24 rounded bg-gray-600" />
      <div className="mt-2 h-4 w-16 rounded bg-gray-500" />
    </div>
    <div className="min-w-0 flex-1 overflow-hidden">
      <div className="flex h-16 flex-row items-center gap-4">
        {Array.from({ length: secondaryCount }, (_, idx) => (
          <div
            key={idx}
            className="flex h-16 min-w-32 shrink-0 animate-pulse flex-col items-center justify-center gap-1 rounded-lg border border-gray-500 bg-gray-700 p-2 opacity-50"
          >
            <div className="h-6 w-24 rounded bg-gray-600" />
            <div className="mt-2 h-4 w-16 rounded bg-gray-500" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const formatDashboardKpiLabel = formatKpiLabel;
